import type React from "react";
import {useEffect, useRef, useState} from "react";
import {useAuthState} from "react-firebase-hooks/auth";

import type {SettingsEntity} from "@repo/shared";

import {
  useCurrentUserSettings,
  useUpdateSettings,
} from "~/api/hooks/useSettings";
import FullscreenButton from "~/component/common/FullscreenButton";
import {auth} from "~/firebase/firebase";

interface BooleanSettingToggleProps {
  label: string;
  settingKey: keyof SettingsEntity;
}

export const BooleanSettingToggle: React.FC<BooleanSettingToggleProps> = ({
  label,
  settingKey,
}) => {
  const [myUser] = useAuthState(auth);
  const {data: settings, isLoading} = useCurrentUserSettings();
  const {mutate: updateSettings} = useUpdateSettings();

  if (isLoading || !settings) {
    return <div>Loading {label}...</div>;
  }

  const currentValue = settings[settingKey] as boolean;

  const handleToggle = () => {
    updateSettings({
      key: {id: myUser?.uid ?? "unknown"},
      data: {...settings, [settingKey]: !currentValue},
    });
  };

  return (
    <div className="grid grid-cols-2 items-center gap-4">
      <label className="text-gray-300 text-sm">{label}</label>
      <input
        type="checkbox"
        checked={currentValue}
        onChange={handleToggle}
        className="form-checkbox h-5 w-5 text-orange-500"
      />
    </div>
  );
};

interface NumericSettingSliderProps {
  label: string;
  settingKey: keyof SettingsEntity;
  min?: number;
  max?: number;
  step?: number;
}

export const NumericSettingSlider: React.FC<NumericSettingSliderProps> = ({
  label,
  settingKey,
  min = 0,
  max = 1,
  step = 0.01,
}) => {
  const [myUser] = useAuthState(auth);
  const {data: settings, isLoading} = useCurrentUserSettings();
  const {mutate: updateSettings} = useUpdateSettings();

  // Local state holds the slider value for instantaneous feedback.
  const [localValue, setLocalValue] = useState<number>(min);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // When settings load (or change externally) update the local state.
  useEffect(() => {
    if (settings) {
      setLocalValue(settings[settingKey] as number);
    }
  }, [settings, settingKey]);

  const handleSliderChange = (newVal: number) => {
    setLocalValue(newVal);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      if (settings) {
        updateSettings({
          key: {id: myUser?.uid ?? "unknown"},
          data: {...settings, [settingKey]: newVal},
        });
      }
    }, 150);
  };

  if (isLoading || !settings) {
    return <div>Loading {label}...</div>;
  }

  return (
    <div className="grid grid-cols-2 items-center gap-4">
      <label className="text-gray-300 text-sm">{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={localValue}
        onChange={(e) => handleSliderChange(Number.parseFloat(e.target.value))}
        className="w-full"
      />
    </div>
  );
};

const SettingsContent: React.FC = () => {
  return (
    <div className="w-full text-white">
      <div className="space-y-4">
        <BooleanSettingToggle label="Show FPS" settingKey="showFps" />
        <BooleanSettingToggle label="Live Replays" settingKey="liveReplays" />
        <BooleanSettingToggle
          label="Player Names"
          settingKey="showPlayerNames"
        />
        <BooleanSettingToggle label="Trails" settingKey="showTrails" />
        <BooleanSettingToggle
          label="PB Ghost"
          settingKey="showPersonalBestGhost"
        />
        <BooleanSettingToggle
          label="Enable Log Viewer"
          settingKey="enableLogViewer"
        />
        <NumericSettingSlider label="Music" settingKey="musicVolume" />
        <NumericSettingSlider label="Sound" settingKey="soundVolume" />
        <div>
          <FullscreenButton />
        </div>
      </div>
    </div>
  );
};

export default SettingsContent;
